import { skipToken } from '@reduxjs/toolkit/query'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router'
import { Arr } from '../../../../helpers/Arr'
import {
  useGetTenantQuery,
  useGetTenantTagsQuery,
  usePostTenantMutation,
  usePutTenantMutation,
} from '../../../../redux/api/customerApi'
import Alert from '../../../common/Alert/Alert'
import './AddNewGroupFunctional.scss'
import { Loader } from '../../../common/Loader/Loader'

interface RouteParams {
  id: string
}

interface TenantForm {
  name: string
  tags: string[]
  isCompany: boolean
  isInsurance: boolean
  isSupplier: boolean
}

const defaultForm: TenantForm = {
  name: '',
  tags: [],
  isCompany: false,
  isInsurance: false,
  isSupplier: false,
}

export default function AddNewGroupFunctional() {
  const { t } = useTranslation('common')
  const history = useHistory()
  const { id } = useParams<RouteParams>()
  const isNew = id === 'new'

  const { data: tenant, isLoading: tenantLoading } = useGetTenantQuery(isNew ? skipToken : id)
  const { data: allTags = [] } = useGetTenantTagsQuery()
  const [postTenant] = usePostTenantMutation()
  const [putTenant] = usePutTenantMutation()

  const [form, setForm] = useState<TenantForm>(defaultForm)
  const [originalFlags, setOriginalFlags] = useState({ isCompany: false, isSupplier: false })
  const [error, setError] = useState('')
  const [tagsSearch, setTagsSearch] = useState('')
  const [conflictError, setConflictError] = useState(false)

  useEffect(() => {
    if (!tenant) return
    setForm({
      name: tenant.name,
      tags: tenant.tags,
      isCompany: tenant.isCompany,
      isInsurance: tenant.isInsurance,
      isSupplier: tenant.isSupplier,
    })
    setOriginalFlags({ isCompany: tenant.isCompany, isSupplier: tenant.isSupplier })
  }, [tenant])

  const validate = (name: string): boolean => {
    if (!name.trim()) {
      setError(t('TENANT_NAME_IS_REQUIRED'))
      return false
    }
    setError('')
    return true
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setForm((f) => ({ ...f, name: value }))
    if (error) validate(value)
  }

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target
    setForm((f) => ({
      ...f,
      [name]: checked,
      ...(name === 'isCompany' && checked ? { isSupplier: false } : {}),
      ...(name === 'isSupplier' && checked ? { isCompany: false } : {}),
    }))
  }

  const applyTag = (tag: string) => setTagsSearch(tag)

  const addTag = () => {
    const trimmed = tagsSearch.trim()
    if (!trimmed) return
    if (form.tags.some((t) => t.toLowerCase() === trimmed.toLowerCase())) return
    setForm((f) => ({ ...f, tags: [...f.tags, trimmed] }))
    setTagsSearch('')
  }

  const removeTag = (tag: string) => setForm((f) => ({ ...f, tags: f.tags.filter((t) => t !== tag) }))

  const filteredTags = useMemo(
    () =>
      tagsSearch ? allTags.filter((t) => t.toLowerCase().includes(tagsSearch.toLowerCase()) && t !== tagsSearch) : [],
    [allTags, tagsSearch]
  )

  const handleSubmit = async () => {
    if (!validate(form.name)) return
    try {
      if (isNew) {
        await postTenant(form).unwrap()
      } else {
        await putTenant({ tenantId: id, model: form }).unwrap()
      }
      history.push('/UserManagement/groups')
    } catch (err) {
      if (typeof err === 'object' && err !== null && 'status' in err && err.status === 409) setConflictError(true)
    }
  }

  //pre-process and disable one of the selectors for existing tenant
  const lockCompany = !isNew && originalFlags.isSupplier
  const lockSupplier = !isNew && originalFlags.isCompany

  return (
    <>
      <Loader blocking={tenantLoading}>
        <div className="addNewGroup">
          <a className="addNewGroup__close" onClick={() => history.goBack()}>
            <i className="icon-close" />
          </a>

          <span className="addNewGroup__label">{t('TENANT_NAME')}:</span>
          <input
            autoComplete="off"
            type="text"
            value={form.name}
            className={`addNewGroup__input${error ? ' addNewGroup__inputError' : ''}`}
            placeholder={t('TENANT_NAME')}
            onChange={handleNameChange}
          />
          {error && <span className="addNewGroup__errorMsg">{error}</span>}

          <span className="addNewGroup__label addNewGroup__label addNewGroup__labelSpaced">{t('TAGS')}:</span>
          <div className="addNewGroup__tags">
            <input
              autoComplete="off"
              type="text"
              value={tagsSearch}
              className="addNewGroup__input"
              placeholder={t('TAGS')}
              onChange={(e) => setTagsSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addTag()}
            />
            <div className="addNewGroup__tagAdd" onClick={addTag}>
              <i className="icon-add" /> TAG
            </div>
            {filteredTags.length > 0 && (
              <ul className="addNewGroup__autocomplete">
                {filteredTags.map((tag) => (
                  <li key={tag} onClick={() => applyTag(tag)}>
                    {tag}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {Arr.any(form.tags) && (
            <ul className="addNewGroup__tagList">
              {form.tags.map((tag) => (
                <li key={tag} className="addNewGroup__tag">
                  <i className="icon-close" onClick={() => removeTag(tag)} />
                  <span>{tag}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="addNewGroup__roles">
            <label>
              <input
                disabled={lockCompany}
                type="checkbox"
                name="isCompany"
                checked={form.isCompany}
                onChange={handleCheckbox}
              />
              {t('CUSTOMER')}
            </label>
            <label>
              <input
                disabled={lockSupplier}
                type="checkbox"
                name="isSupplier"
                checked={form.isSupplier}
                onChange={handleCheckbox}
              />
              {t('SUPPLIER')}
            </label>
            <label>
              <input type="checkbox" name="isInsurance" checked={form.isInsurance} onChange={handleCheckbox} />
              {t('INSURANCE')}
            </label>
          </div>

          <div className="addNewGroup__submit">
            <button type="button" onClick={handleSubmit}>
              {t('SAVE')}
            </button>
          </div>
        </div>
      </Loader>

      <Alert
        onClose={() => setConflictError(false)}
        visible={conflictError}
        title={t('API_DATA_VALIDATION_ERROR_TITLE')}
        message={t('API_DATA_VALIDATION_ERROR_MSG')}
      />
      <div className="blur" />
    </>
  )
}
